import { PackageOpen } from "lucide-react";

/**
 * Reusable empty state with a subtle SVG illustration.
 * Props: icon (Component), title, subtitle, action (ReactNode), variant ("package" | "bell" | "users" | "audit")
 */
export default function EmptyState({ icon: Icon = PackageOpen, title, subtitle, action, variant = "package", testid = "empty-state" }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div className="mb-6">
        <Illustration variant={variant} />
      </div>
      <div className="flex items-center gap-2 text-primary mb-2">
        <Icon size={16} />
        <span className="overline text-primary/80">{title}</span>
      </div>
      <h3 className="font-display text-2xl mb-2">{subtitle || "Nothing to show yet"}</h3>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

function Illustration({ variant }) {
  // Distinct enterprise-style line illustrations
  const stroke = "hsl(var(--primary))";
  const muted = "hsl(var(--muted-foreground))";
  const fill = "hsl(var(--primary) / 0.08)";
  const cyan = "hsl(var(--accent))";
  const common = { width: 200, height: 140, viewBox: "0 0 200 140", fill: "none", xmlns: "http://www.w3.org/2000/svg" };

  if (variant === "bell") {
    return (
      <svg {...common} aria-hidden>
        <ellipse cx="100" cy="128" rx="60" ry="4" fill={muted} opacity="0.15" />
        <path d="M60 90 L60 60 A40 40 0 0 1 140 60 L140 90 L150 100 L50 100 Z" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <path d="M90 100 A10 10 0 0 0 110 100" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="100" cy="65" r="6" fill={cyan} />
        <path d="M100 25 L100 20" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
        <path d="M75 40 L69 34" stroke={muted} strokeWidth="1" strokeLinecap="round" opacity="0.6" />
        <path d="M125 40 L131 34" stroke={muted} strokeWidth="1" strokeLinecap="round" opacity="0.6" />
      </svg>
    );
  }

  if (variant === "users") {
    return (
      <svg {...common} aria-hidden>
        <ellipse cx="100" cy="128" rx="60" ry="4" fill={muted} opacity="0.15" />
        <rect x="30" y="55" width="140" height="70" rx="4" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <circle cx="70" cy="80" r="12" fill="hsl(var(--card))" stroke={stroke} strokeWidth="1.5" />
        <path d="M50 108 A20 20 0 0 1 90 108" stroke={stroke} strokeWidth="1.5" fill="none" />
        <rect x="100" y="72" width="55" height="4" rx="2" fill={muted} opacity="0.4" />
        <rect x="100" y="84" width="40" height="4" rx="2" fill={muted} opacity="0.3" />
        <rect x="100" y="100" width="50" height="4" rx="2" fill={muted} opacity="0.3" />
        <circle cx="155" cy="45" r="18" fill={cyan} opacity="0.15" />
        <circle cx="155" cy="45" r="8" fill={cyan} />
        <path d="M151 45 L154 48 L159 42" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (variant === "audit") {
    return (
      <svg {...common} aria-hidden>
        <ellipse cx="100" cy="128" rx="60" ry="4" fill={muted} opacity="0.15" />
        <rect x="45" y="25" width="110" height="95" rx="4" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <rect x="55" y="45" width="60" height="4" rx="2" fill={muted} opacity="0.5" />
        <rect x="55" y="58" width="80" height="3" rx="1.5" fill={muted} opacity="0.3" />
        <rect x="55" y="68" width="70" height="3" rx="1.5" fill={muted} opacity="0.3" />
        <rect x="55" y="85" width="50" height="4" rx="2" fill={muted} opacity="0.5" />
        <rect x="55" y="98" width="80" height="3" rx="1.5" fill={muted} opacity="0.3" />
        <circle cx="140" cy="45" r="8" fill={cyan} />
        <path d="M136 45 L139 48 L144 42" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  // default: package
  return (
    <svg {...common} aria-hidden>
      <ellipse cx="100" cy="128" rx="60" ry="4" fill={muted} opacity="0.15" />
      <path d="M100 40 L60 60 L60 105 L100 125 L140 105 L140 60 Z" fill={fill} stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M60 60 L100 80 L140 60" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M100 80 L100 125" stroke={stroke} strokeWidth="1.5" />
      <rect x="80" y="52" width="40" height="8" fill={cyan} opacity="0.5" rx="1" />
      <rect x="80" y="52" width="40" height="8" fill="none" stroke={stroke} strokeWidth="1" rx="1" />
      <circle cx="150" cy="45" r="4" fill={cyan} />
      <path d="M150 30 L150 20" stroke={stroke} strokeWidth="1" strokeLinecap="round" opacity="0.4" />
      <path d="M158 38 L165 34" stroke={stroke} strokeWidth="1" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}
